import React, { useEffect } from 'react';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { 
  Camera, 
  QrCode, 
  Lock, 
  Zap, 
  Save, 
  WifiOff,
  Github,
  Download,
  Shield,
  Database,
  Eye,
  HardDrive,
  Book,
  Globe,
  Smartphone,
  CheckCircle,
  AlertTriangle
} from 'lucide-react';

const Home = () => {
  const [mobileMenuOpen, setMobileMenuOpen] = React.useState(false);

  useEffect(() => {
    const observerOptions = {
      threshold: 0.1,
      rootMargin: '0px 0px -50px 0px'
    };

    const observer = new IntersectionObserver((entries) => {
      entries.forEach(entry => {
        if (entry.isIntersecting) {
          entry.target.classList.add('fade-in-view');
        }
      });
    }, observerOptions);

    document.querySelectorAll('.fade-in-scroll').forEach(el => {
      observer.observe(el);
    });

    return () => observer.disconnect();
  }, []);

  const features = [
    {
      icon: <Camera className="w-10 h-10" />,
      title: 'Scan Anything',
      description: 'Scan QR codes, barcodes, and all major 1D/2D code formats instantly with high accuracy using advanced ML Kit technology.'
    },
    {
      icon: <QrCode className="w-10 h-10" />,
      title: 'Generate QR Codes',
      description: 'Create custom QR codes for text, URLs, WiFi, contacts, and more. Beautiful and functional design.'
    },
    {
      icon: <Lock className="w-10 h-10" />,
      title: 'Complete Privacy',
      description: 'All processing happens locally on your device. Zero data collection, no tracking, no analytics.'
    },
    {
      icon: <Zap className="w-10 h-10" />,
      title: 'Lightning Fast',
      description: 'Instant scanning and generation with optimized performance. No delays, no waiting.'
    },
    {
      icon: <Save className="w-10 h-10" />,
      title: 'Save & Share',
      description: 'Keep history of scanned codes, save generated QR codes, and share them easily with others.'
    },
    {
      icon: <WifiOff className="w-10 h-10" />,
      title: '100% Offline',
      description: 'No internet connection required. Works perfectly offline, anytime, anywhere.'
    }
  ];

  const privacyFeatures = [
    {
      icon: <Shield />,
      title: 'No Data Collection',
      description: 'QR & Barcode Scanner does not collect, store, transmit, or share any personal data whatsoever. The app operates completely offline and does not require an internet connection.'
    },
    {
      icon: <Database />,
      title: 'Local Data Storage',
      description: 'All scanned and generated QR codes are stored exclusively on your device. This data never leaves your device and is not accessible to anyone except you.'
    },
    {
      icon: <Eye />,
      title: 'Camera Permission',
      description: 'Camera permission is required solely for scanning QR codes and barcodes in real-time. The app does not save, record, or transmit any images or video from your camera.'
    },
    {
      icon: <HardDrive />,
      title: 'Storage Permission',
      description: 'Storage permission is used only to save generated QR code images to your device when you choose to export them. You have complete control over what gets saved.'
    },
    {
      icon: <Book />,
      title: 'Third-Party Libraries',
      description: 'Uses open-source libraries (ZXing and Google ML Kit) for QR code processing. These libraries process all data locally on your device.'
    },
    {
      icon: <Globe />,
      title: 'No Internet Access',
      description: 'This app does not use or require internet access. All functionality works completely offline. Your data stays on your device, always.'
    }
  ];

  const licensePoints = [
    { type: 'check', text: 'You can freely use this software for any purpose' },
    { type: 'check', text: 'You can study how the app works by viewing the source code' },
    { type: 'check', text: 'You can modify the software to suit your needs' },
    { type: 'check', text: 'You can distribute copies to help others' },
    { type: 'warning', text: 'If you distribute modified versions, you must license them under GPL v3.0' },
    { type: 'warning', text: 'You must provide access to the source code' },
    { type: 'warning', text: 'You cannot make proprietary/closed-source versions' }
  ];

  const scrollToSection = (id) => {
    const element = document.getElementById(id);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth', block: 'start' });
      setMobileMenuOpen(false);
    }
  };

  return (
    <div className="min-h-screen">
      {/* Navigation */}
      <nav className="fixed top-0 w-full bg-white/95 backdrop-blur-lg shadow-sm z-50 border-b border-gray-100">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-16">
            <div className="flex items-center space-x-2">
              <Smartphone className="w-6 h-6 text-coral-600" />
              <span className="text-xl font-bold text-gray-900">QR & Barcode Scanner</span>
            </div>
            <div className="hidden md:flex space-x-8">
              <button onClick={() => scrollToSection('features')} className="text-gray-700 hover:text-coral-600 font-medium transition-colors">Features</button>
              <button onClick={() => scrollToSection('open-source')} className="text-gray-700 hover:text-coral-600 font-medium transition-colors">Open Source</button>
              <button onClick={() => scrollToSection('privacy')} className="text-gray-700 hover:text-coral-600 font-medium transition-colors">Privacy</button>
              <button onClick={() => scrollToSection('download')} className="text-gray-700 hover:text-coral-600 font-medium transition-colors">Download</button>
            </div>
            <button 
              className="md:hidden p-2"
              onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
            >
              {mobileMenuOpen ? '✕' : '☰'}
            </button>
          </div>
          {mobileMenuOpen && (
            <div className="md:hidden py-4 space-y-2 border-t border-gray-100">
              <button onClick={() => scrollToSection('features')} className="block w-full text-left py-2 text-gray-700 hover:text-coral-600 font-medium">Features</button>
              <button onClick={() => scrollToSection('open-source')} className="block w-full text-left py-2 text-gray-700 hover:text-coral-600 font-medium">Open Source</button>
              <button onClick={() => scrollToSection('privacy')} className="block w-full text-left py-2 text-gray-700 hover:text-coral-600 font-medium">Privacy</button>
              <button onClick={() => scrollToSection('download')} className="block w-full text-left py-2 text-gray-700 hover:text-coral-600 font-medium">Download</button>
            </div>
          )}
        </div>
      </nav>

      {/* Hero Section */}
      <section className="pt-32 pb-20 px-4 sm:px-6 lg:px-8 bg-gradient-to-br from-coral-50 via-rose-50 to-amber-50 relative overflow-hidden">
        <div className="absolute inset-0 opacity-10">
          <div className="absolute top-0 left-0 w-96 h-96 bg-coral-300 rounded-full blur-3xl"></div>
          <div className="absolute bottom-0 right-0 w-96 h-96 bg-teal-300 rounded-full blur-3xl"></div>
        </div>
        <div className="max-w-7xl mx-auto relative z-10">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div className="text-center lg:text-left">
              <div className="inline-flex items-center space-x-2 bg-white/80 backdrop-blur-sm px-4 py-2 rounded-full mb-6 shadow-sm">
                <CheckCircle className="w-4 h-4 text-teal-600" />
                <span className="text-sm font-semibold text-gray-700">Free & Open Source</span>
              </div>
              <h1 className="text-5xl md:text-6xl font-bold text-gray-900 mb-6 leading-tight">
                QR & Barcode Scanner
              </h1>
              <p className="text-2xl text-gray-700 mb-4 font-medium">Free & Open Source Scanner for Android</p>
              <p className="text-lg text-gray-600 mb-8">Scan and generate QR codes and barcodes with complete privacy and no ads</p>
              
              <div className="flex flex-wrap gap-3 justify-center lg:justify-start mb-8">
                <Badge variant="secondary" className="px-4 py-2 text-sm bg-white/90 hover:bg-white">FREE</Badge>
                <Badge variant="secondary" className="px-4 py-2 text-sm bg-white/90 hover:bg-white">OPEN SOURCE</Badge>
                <Badge variant="secondary" className="px-4 py-2 text-sm bg-white/90 hover:bg-white">NO ADS</Badge>
                <Badge variant="secondary" className="px-4 py-2 text-sm bg-white/90 hover:bg-white">OFFLINE</Badge>
              </div>

              <div className="flex flex-wrap gap-4 justify-center lg:justify-start">
                <Button size="lg" className="bg-coral-600 hover:bg-coral-700 text-white px-8 py-6 text-lg shadow-lg hover:shadow-xl transition-all" onClick={() => scrollToSection('download')}>
                  <Download className="w-5 h-5 mr-2" />
                  Download Now
                </Button>
                <Button size="lg" variant="outline" className="border-2 border-gray-800 text-gray-800 hover:bg-gray-800 hover:text-white px-8 py-6 text-lg" onClick={() => window.open('https://github.com/publicappsrepo/qrbarcode-scanner', '_blank')}>
                  <Github className="w-5 h-5 mr-2" />
                  View Source
                </Button>
              </div>
            </div>
            <div className="flex justify-center lg:justify-end">
              <div className="relative">
                <img 
                  src="https://customer-assets.emergentagent.com/job_4026e6a0-8007-417e-bc80-888104247a51/artifacts/s2fzl1uz_1.png" 
                  alt="QR Scanner App Screenshot" 
                  className="w-80 md:w-96 rounded-3xl shadow-2xl transform hover:scale-105 transition-transform duration-300"
                />
                <div className="absolute -bottom-4 -right-4 w-64 h-64 bg-teal-200 rounded-full blur-3xl opacity-30 -z-10"></div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section id="features" className="py-20 px-4 sm:px-6 lg:px-8 bg-white">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-16">
            <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-4">Why Choose QR & Barcode Scanner?</h2>
            <p className="text-xl text-gray-600">Powerful features for all your scanning needs</p>
          </div>
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            {features.map((feature, index) => (
              <Card key={index} className="fade-in-scroll border-0 shadow-lg hover:shadow-xl transition-all duration-300 hover:-translate-y-2 bg-gradient-to-br from-white to-gray-50">
                <CardHeader>
                  <div className="w-16 h-16 bg-gradient-to-br from-coral-100 to-teal-100 rounded-2xl flex items-center justify-center mb-4 text-coral-600">
                    {feature.icon}
                  </div>
                  <CardTitle className="text-2xl text-gray-900">{feature.title}</CardTitle>
                </CardHeader>
                <CardContent>
                  <CardDescription className="text-gray-600 text-base leading-relaxed">{feature.description}</CardDescription>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* App Screenshots Showcase */}
      <section className="py-20 px-4 sm:px-6 lg:px-8 bg-gradient-to-br from-gray-50 to-gray-100">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-16">
            <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-4">Beautiful & Intuitive Design</h2>
            <p className="text-xl text-gray-600">Experience seamless scanning and generation</p>
          </div>
          <div className="grid md:grid-cols-3 gap-8 items-center">
            <div className="fade-in-scroll transform hover:scale-105 transition-transform duration-300">
              <img 
                src="https://customer-assets.emergentagent.com/job_4026e6a0-8007-417e-bc80-888104247a51/artifacts/10di3yvx_2.png" 
                alt="QR Code Generation" 
                className="w-full rounded-2xl shadow-2xl"
              />
              <p className="text-center mt-4 text-lg font-semibold text-gray-700">Create QR Codes</p>
            </div>
            <div className="fade-in-scroll transform hover:scale-105 transition-transform duration-300">
              <img 
                src="https://customer-assets.emergentagent.com/job_4026e6a0-8007-417e-bc80-888104247a51/artifacts/uwjjptez_unnamed%20%282%29.png" 
                alt="Smart Settings" 
                className="w-full rounded-2xl shadow-2xl"
              />
              <p className="text-center mt-4 text-lg font-semibold text-gray-700">Smart Settings</p>
            </div>
            <div className="fade-in-scroll transform hover:scale-105 transition-transform duration-300">
              <img 
                src="https://customer-assets.emergentagent.com/job_4026e6a0-8007-417e-bc80-888104247a51/artifacts/vcg0i8r7_3.png" 
                alt="Code Details" 
                className="w-full rounded-2xl shadow-2xl"
              />
              <p className="text-center mt-4 text-lg font-semibold text-gray-700">Instant Actions</p>
            </div>
          </div>
        </div>
      </section>

      {/* Open Source Section */}
      <section id="open-source" className="py-20 px-4 sm:px-6 lg:px-8 bg-gradient-to-br from-coral-600 to-rose-600 text-white relative overflow-hidden">
        <div className="absolute inset-0 opacity-10">
          <div className="absolute top-10 right-10 w-64 h-64 bg-white rounded-full blur-3xl"></div>
          <div className="absolute bottom-10 left-10 w-80 h-80 bg-white rounded-full blur-3xl"></div>
        </div>
        <div className="max-w-5xl mx-auto relative z-10">
          <div className="text-center mb-12">
            <div className="inline-flex items-center space-x-2 bg-white/20 backdrop-blur-sm px-4 py-2 rounded-full mb-6">
              <Book className="w-5 h-5" />
              <span className="font-semibold">Open Source Software</span>
            </div>
            <h2 className="text-4xl md:text-5xl font-bold mb-6">Free & Open Source Software</h2>
            <p className="text-xl leading-relaxed mb-4 text-white/95">
              QR & Barcode Scanner is licensed under <strong>GNU General Public License v3.0</strong>, making it completely free with source code available for everyone to inspect, modify, and improve.
            </p>
            <p className="text-lg text-white/90 mb-8">
              Project repository: <code className="bg-white/20 px-3 py-1 rounded font-mono">github.com/publicappsrepo/qrbarcode-scanner/</code>
            </p>
          </div>

          <Card className="bg-white/95 backdrop-blur-sm border-0 shadow-2xl">
            <CardHeader>
              <CardTitle className="text-3xl text-gray-900">What does GPL v3.0 mean for you?</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-4">
                {licensePoints.map((point, index) => (
                  <div key={index} className="flex items-start space-x-3">
                    <div className="flex-shrink-0 mt-1">
                      {point.type === 'check' ? (
                        <CheckCircle className="w-6 h-6 text-teal-600" />
                      ) : (
                        <AlertTriangle className="w-6 h-6 text-amber-600" />
                      )}
                    </div>
                    <p className="text-gray-700 text-lg">{point.text}</p>
                  </div>
                ))}
              </div>
              <div className="flex flex-wrap gap-4 justify-center mt-8">
                <Button size="lg" className="bg-gray-900 hover:bg-gray-800 text-white px-8" onClick={() => window.open('https://github.com/publicappsrepo/qrbarcode-scanner', '_blank')}>
                  <Github className="w-5 h-5 mr-2" />
                  View on GitHub
                </Button>
                <Button size="lg" variant="outline" className="border-2 border-coral-600 text-coral-600 hover:bg-coral-50 px-8" onClick={() => window.open('https://www.gnu.org/licenses/gpl-3.0.html', '_blank')}>
                  <Book className="w-5 h-5 mr-2" />
                  Read Full License
                </Button>
              </div>
            </CardContent>
          </Card>
        </div>
      </section>

      {/* Privacy Section */}
      <section id="privacy" className="py-20 px-4 sm:px-6 lg:px-8 bg-white">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-16">
            <div className="inline-flex items-center space-x-2 bg-teal-100 px-4 py-2 rounded-full mb-6">
              <Lock className="w-5 h-5 text-teal-700" />
              <span className="font-semibold text-teal-700">Privacy First</span>
            </div>
            <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-6">Your Privacy is Protected</h2>
          </div>

          <div className="max-w-4xl mx-auto mb-12">
            <Card className="border-l-4 border-l-teal-600 shadow-xl bg-gradient-to-br from-teal-50 to-cyan-50">
              <CardHeader>
                <CardTitle className="text-3xl text-gray-900">Complete Privacy Guarantee</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="grid sm:grid-cols-2 gap-4 mb-6">
                  <div className="flex items-center space-x-2">
                    <CheckCircle className="w-5 h-5 text-teal-600 flex-shrink-0" />
                    <span className="text-gray-700 font-medium">No internet required</span>
                  </div>
                  <div className="flex items-center space-x-2">
                    <CheckCircle className="w-5 h-5 text-teal-600 flex-shrink-0" />
                    <span className="text-gray-700 font-medium">No data collection</span>
                  </div>
                  <div className="flex items-center space-x-2">
                    <CheckCircle className="w-5 h-5 text-teal-600 flex-shrink-0" />
                    <span className="text-gray-700 font-medium">No analytics or tracking</span>
                  </div>
                  <div className="flex items-center space-x-2">
                    <CheckCircle className="w-5 h-5 text-teal-600 flex-shrink-0" />
                    <span className="text-gray-700 font-medium">100% offline operation</span>
                  </div>
                </div>
                <Badge className="bg-teal-600 hover:bg-teal-700 text-white px-4 py-2 text-sm">COMPLETELY PRIVATE</Badge>
              </CardContent>
            </Card>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {privacyFeatures.map((feature, index) => (
              <Card key={index} className="fade-in-scroll border-0 shadow-lg hover:shadow-xl transition-all duration-300">
                <CardHeader>
                  <div className="w-12 h-12 bg-teal-100 rounded-xl flex items-center justify-center mb-3 text-teal-700">
                    {feature.icon}
                  </div>
                  <CardTitle className="text-xl text-gray-900">{feature.title}</CardTitle>
                </CardHeader>
                <CardContent>
                  <CardDescription className="text-gray-600 leading-relaxed">{feature.description}</CardDescription>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Download Section */}
      <section id="download" className="py-20 px-4 sm:px-6 lg:px-8 bg-gradient-to-br from-gray-50 to-gray-100">
        <div className="max-w-4xl mx-auto text-center">
          <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-6">Download QR & Barcode Scanner</h2>
          <p className="text-xl text-gray-600 mb-12">Get the app for free from Google Play Store or download the APK directly</p>
          
          <div className="flex flex-wrap gap-6 justify-center mb-8">
            <Button size="lg" className="bg-coral-600 hover:bg-coral-700 text-white px-10 py-7 text-lg shadow-xl hover:shadow-2xl transition-all" onClick={() => window.open('https://play.google.com/store/apps/details?id=com.appsease.qrbarcode', '_blank')}>
              <Smartphone className="w-6 h-6 mr-2" />
              Get it on Google Play
            </Button>
            <Button size="lg" variant="outline" className="border-2 border-gray-800 text-gray-800 hover:bg-gray-800 hover:text-white px-10 py-7 text-lg" onClick={() => window.open('https://github.com/publicappsrepo/qrbarcode-scanner/releases', '_blank')}>
              <Download className="w-6 h-6 mr-2" />
              Download APK
            </Button>
          </div>
          
          <p className="text-gray-600">
            Or build it yourself from <a href="https://github.com/publicappsrepo/qrbarcode-scanner" className="text-coral-600 hover:text-coral-700 font-semibold underline" target="_blank" rel="noopener noreferrer">source code</a>
          </p>
        </div>
      </section>

      {/* Copyright Section */}
      <section className="py-20 px-4 sm:px-6 lg:px-8 bg-gray-900 text-white">
        <div className="max-w-4xl mx-auto">
          <h2 className="text-3xl md:text-4xl font-bold text-center mb-8">Copyright & License</h2>
          <Card className="bg-gray-800/50 backdrop-blur-sm border-l-4 border-l-coral-500 shadow-2xl">
            <CardHeader>
              <CardTitle className="text-2xl text-white">GNU General Public License v3.0</CardTitle>
            </CardHeader>
            <CardContent>
              <pre className="text-gray-300 text-sm leading-relaxed whitespace-pre-wrap font-mono">
{`Copyright (C) 2025 K M Rejowan Ahmmed

This program is free software: you can redistribute it and/or 
modify it under the terms of the GNU General Public License as 
published by the Free Software Foundation, either version 3 
of the License, or (at your option) any later version.

This program is distributed in the hope that it will be useful, 
but WITHOUT ANY WARRANTY; without even the implied warranty of 
MERCHANTABILITY or FITNESS FOR A PARTICULAR PURPOSE. See the 
GNU General Public License for more details.

You should have received a copy of the GNU General Public
License along with this program. If not, see
<https://www.gnu.org/licenses/>.`}
              </pre>
            </CardContent>
          </Card>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-gray-950 text-white py-12 px-4 sm:px-6 lg:px-8">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-8">
            <p className="text-xl font-semibold mb-4">QR & Barcode Scanner - Made with ❤️ by Appsease</p>
            <div className="flex flex-wrap gap-6 justify-center mb-6">
              <a href="https://github.com/publicappsrepo/qrbarcode-scanner" className="text-coral-400 hover:text-coral-300 transition-colors" target="_blank" rel="noopener noreferrer">GitHub</a>
              <a href="https://github.com/publicappsrepo/qrbarcode-scanner/issues" className="text-coral-400 hover:text-coral-300 transition-colors" target="_blank" rel="noopener noreferrer">Report Issue</a>
              <button onClick={() => scrollToSection('privacy')} className="text-coral-400 hover:text-coral-300 transition-colors">Privacy Policy</button>
              <a href="mailto:appsease2001@gmail.com" className="text-coral-400 hover:text-coral-300 transition-colors">Contact</a>
            </div>
          </div>
          <div className="border-t border-gray-800 pt-8 text-center">
            <p className="text-gray-400">Licensed under GNU GPL v3.0 | © 2025 All Rights Reserved</p>
            <p className="text-gray-500 text-sm mt-2">Last updated: December 28, 2025</p>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default Home;